import _ from 'lodash';
import Storage from '../../../core/utils/Storage';
import Radio from 'backbone.radio';
import Backbone from 'backbone';

import { AppRouter } from 'backbone.marionette';
import { User } from '../../models';
import { isAuthorized, redirectTo, sendDataLayer } from '../../../core/utils';

const menuChannel = Radio.channel( 'menu' );
const appViewChannel = Radio.channel('appView');
const matchCentreGameBarChannel = Radio.channel('matchCentreGameBar');

export default class Router extends AppRouter {
	constructor( args ) {
		super( args );

		/**
		 * Auth object to configure which route should be under authorization
		 * @type {object}
		 */
		this.authOnly = _.result( this, "authOnly", {} );

		/**
		 * Auth object to configure which route required to be not under authorization
		 * @type {object}
		 */
		this.guestOnly = _.result( this, "guestOnly", {} );

		/**
		 * Define to check for authorization on every route or not
		 * @type {boolean}
		 */
		this.authOnlyDefault = true;

		/**
		 * URL where user will be redirected if his not legged in and auth is required.
		 * @type {string}
		 */
		this.authOnlyRedirectUrl = '/';

		/**
		 * URL where user will be redirected in case
		 * he is authorized and route is only for guests.
		 * @type {string}
		 */
		this.guestOnlyRedirectUrl = '/';
	}

	/**
	 * Check the `auth` hash for a callback. Returns `authDefault` if
	 * the callback is not specified.
	 * @param  {String} callbackName name of the function.
	 * @return {Boolean} true if the callback is private.
	 */
	hasAuth( callbackName ) {
		return _.result( this.authOnly, callbackName, this.authOnlyDefault );
	}

	/**
	 * Check if selected router are for guests only
	 * @param callbackName
	 * @return {Boolean} true if the callback is for not authorized users only.
	 */
	isGuestOnly( callbackName ) {
		return _.result( this.guestOnly, callbackName, false );
	}

	isAuthorized() {
		return isAuthorized();
	}

	isGameRecovered() {
		return User.isGameRecovered();
	}

	initMenu( args, name ) {
		let menu_item = this.typeMenu,
			data      = {
				args,
				name,
				menu_item
			};

		menuChannel.trigger( 'init:menu', data );
	}

	get typeMenu() {
		return ( this.menuName ) ? this.menuName : '';
	}

	set typeMenu( type ) {
		this.menuName = type;
	}

	destroyGoogleSlots() {
		if( window.googletag ) {
			window.googletag.cmd.push(function () {
				window.googletag.destroySlots();
			});
		}

		return this;
	}

	checkDataLayer(fragment, name) {
		if( fragment !== '' ) {
			sendDataLayer( {
				'event':     'virtual_page_view',
				'page_url':  fragment,
				'page_name': name
			} );
		}
	}

	isAuthOnly(name) {
		return this.hasAuth( name ) && !this.isAuthorized();
	}

	isReturnToActivate(name) {
		return this.hasAuth( name ) &&
			this.isAuthorized() &&
			name !== 'activateGame' &&
			!this.isGameRecovered();
	}

	isGuestOnlyRedirect(name) {
		return this.isGuestOnly( name ) && this.isAuthorized();
	}

	isAppView(args) {
		if (args.length && args.includes('app_view')) {
			appViewChannel.trigger('hide', true);
		}
	}

	/**
	 * Prevent to change route in case route required auth and user was not logged in.
	 * @param {function} callback
	 * @param {object} args
	 * @param {string} name
	 * @return {boolean}
	 */
	// eslint-disable-next-line complexity
	execute( callback, args, name ) {
		this.isAppView(args);
		const user_requested_route_storage_key = "user_requested_route_before_auth";
		this.destroyGoogleSlots();

		this.checkDataLayer(Backbone.history.fragment, name);

		const is_auth_only = this.isAuthOnly(name);
		if ( is_auth_only ) {
			Storage.clear();

			// Capture user requested route pre-login
			// (Redirect user here once they are authenticated)
			Storage.set(user_requested_route_storage_key, location.pathname);

			redirectTo( this.authOnlyRedirectUrl );

			return false;
		}

		const is_return_to_activate = this.isReturnToActivate(name);

		// When game has NOT been recovered
		if ( is_return_to_activate ) {
			redirectTo( '/?returning=true' );

			return false;
		}

		const is_guest_only = this.isGuestOnlyRedirect(name);

		if ( is_guest_only ) {
			redirectTo( this.guestOnlyRedirectUrl );

			return false;
		}

		// Check for user requested route pre-login and redirect the user if authenticated
		const user_requested_route = Storage.get(user_requested_route_storage_key);
		if (this.isAuthorized() && user_requested_route) {
			Storage.remove(user_requested_route_storage_key);
			redirectTo(user_requested_route);
			return false;
		}

		this.checkForCallback(callback, args);

		this.initMenu( args, name );
		this.initMatchCentreGameBar(name);
	}

	initMatchCentreGameBar(name) {
		matchCentreGameBarChannel.trigger('route:changed', {
			is_authorized: this.isAuthorized(),
			name
		})
	}

	checkForCallback(callback, args) {
		if ( callback ) {
			callback.apply( this, args );
		}
	}
}